<template>
	<div>
		<div class="row content-header">
			<div class="content-header-left mb-2 col-md-9 col-12">
				<div class="row breadcrumbs-top">
					<div class="col-12 d-flex align-items-center">
						<feather-icon icon="ArrowLeftIcon" size="24" class="mr-2" @click="goBack" />
						<h2 class="content-header-title mb-0">Edit Formula</h2>
					</div>
				</div>
			</div>
		</div>

		<validation-observer ref="observer">
			<b-form @submit="updateFormula" method="post">
				<b-card>
					<b-row>
						<!-- Feed -->
						<b-col md="6">
							<b-form-group>
								<template #label>Feed <span class="text-danger">*</span></template>
								<validation-provider #default="{ errors }" name="Feed" rules="required">
									<v-select
										v-model="form.feed_id"
										:options="feedOptions"
										label="name"
										:reduce="reduceId"
										track-by="id"
										:clearable="false"
									/>
									<small class="text-danger">{{ errors[0] }}</small>
								</validation-provider>
							</b-form-group>
						</b-col>

						<!-- Version -->
						<b-col md="3">
							<b-form-group>
								<template #label>Version <span class="text-danger">*</span></template>
								<validation-provider
									#default="{ errors }"
									name="Version"
									:rules="{ required: true, regex: versionPattern }"
								>
									<b-form-input v-model="form.version" placeholder="e.g. 1" type="text" maxlength="6" />
									<small class="text-danger">{{ errors[0] }}</small>
								</validation-provider>
							</b-form-group>
						</b-col>

						<!-- Active -->
						<b-col md="3" class="d-flex align-items-center">
							<b-form-group label="Active" class="mb-0 w-100">
								<b-form-checkbox switch v-model="form.is_active" name="is_active">
									{{ form.is_active ? "Yes" : "No" }}
								</b-form-checkbox>
							</b-form-group>
						</b-col>
					</b-row>

					<!-- Items header -->
					<div class="d-flex align-items-center mt-2 mb-1">
						<h5 class="mb-0">Items</h5>
						<b-button size="sm" variant="primary" class="ml-2" @click="addItem">+ Add Row</b-button>
						<b-badge class="ml-auto" variant="light-primary">Total: {{ totalPercentage.toFixed(2) }}%</b-badge>
					</div>

					<b-alert v-if="!isTotalValid" show variant="warning" class="py-1 px-2 mb-2">
						Percentages must sum to <strong>100%</strong>.
					</b-alert>

					<b-alert v-if="hasDuplicateSelection" show variant="warning" class="py-1 px-2 mb-2">
						Each raw material must be unique.
					</b-alert>

					<!-- Items table -->
					<div class="table-responsive overflow-visible">
						<table class="table table-sm">
							<thead>
								<tr>
									<th style="width: 48%">Raw Material</th>
									<th style="width: 32%">Percentage (%)</th>
									<th style="width: 20%" class="text-right">Action</th>
								</tr>
							</thead>

							<!-- ✅ FIX: bind v-select to the whole option object (NOT id). This makes labels always show. -->
							<tbody>
								<tr v-for="(row, idx) in form.items" :key="row.uid">
									<td>
										<validation-provider :name="'Raw Material ' + (idx + 1)" rules="required" v-slot="{ errors }">
											<v-select
												v-model="row.raw_material"
												:options="filteredRawMaterialOptions(idx)"
												label="name"
												:clearable="false"
												:append-to-body="true"
												:close-on-scroll="true"
											>
												<template #option="{ option }">
													<div class="d-flex justify-content-between w-100">
														<span>{{ option.name }}</span>
														<small v-if="option.unit && option.unit.name" class="text-muted"
															>({{ option.unit.name }})</small
														>
													</div>
												</template>
												<template #selected-option="{ option }">
													<span>{{ option && option.name ? option.name : "" }}</span>
												</template>
											</v-select>
											<small class="text-danger">{{ errors[0] }}</small>
										</validation-provider>
									</td>

									<td>
										<validation-provider
											:name="'Percentage ' + (idx + 1)"
											:rules="{ required: true, regex: percentagePattern }"
											v-slot="{ errors }"
										>
											<b-input-group>
												<b-form-input
													v-model="row.percentage"
													type="number"
													step="0.01"
													min="0"
													max="100"
													inputmode="decimal"
													placeholder="0.00"
													@blur="normalizeRowPercentage(row)"
												/>
												<b-input-group-append>
													<b-input-group-text>%</b-input-group-text>
												</b-input-group-append>
											</b-input-group>
											<small class="text-danger">{{ errors[0] }}</small>
										</validation-provider>
									</td>

									<td class="text-right">
										<b-button size="sm" variant="danger" @click="removeItem(idx)" :disabled="form.items.length === 1">
											Remove
										</b-button>
									</td>
								</tr>
							</tbody>
						</table>
					</div>

					<!-- Submit -->
					<div class="mt-2">
						<b-button
							type="submit"
							variant="primary"
							class="mr-1"
							:disabled="submitting || !isTotalValid || form.items.length === 0 || hasDuplicateSelection"
						>
							{{ submitting ? "Saving..." : "Save Changes" }}
						</b-button>
						<b-button type="button" variant="outline-secondary" @click="goBack">Cancel</b-button>
					</div>
				</b-card>
			</b-form>
		</validation-observer>
	</div>
</template>

<script>
import vSelect from "vue-select";
import axiosIns from "@/libs/axios";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";

export default {
	components: { vSelect, ValidationProvider, ValidationObserver },
	data() {
		return {
			userData: null,
			submitting: false,
			formulaId: null,

			feedOptions: [],
			rawMaterialOptions: [],

			versionPattern: /^\d+$/,
			percentagePattern: /^\d+(\.\d{1,2})?$/,

			form: {
				feed_id: null,
				is_active: true,
				version: "1",
				// ✅ store selected option object, not raw_material_id
				items: [{ uid: Date.now(), raw_material: null, percentage: "" }],
			},
		};
	},
	computed: {
		totalPercentage() {
			return this.form.items.reduce(function (sum, r) {
				var n = parseFloat((r.percentage || "").toString());
				return sum + (isNaN(n) ? 0 : n);
			}, 0);
		},
		isTotalValid() {
			return Math.abs(this.totalPercentage - 100) < 0.001;
		},
		selectedRawMaterialIds() {
			return this.form.items
				.map(function (r) {
					return r && r.raw_material && r.raw_material.id != null ? Number(r.raw_material.id) : null;
				})
				.filter(function (id) {
					return id != null && !Number.isNaN(id);
				});
		},
		hasDuplicateSelection() {
			var seen = new Set();
			for (var i = 0; i < this.selectedRawMaterialIds.length; i++) {
				var id = this.selectedRawMaterialIds[i];
				if (seen.has(id)) return true;
				seen.add(id);
			}
			return false;
		},
	},
	created() {
		this.userData = JSON.parse(sessionStorage.getItem("userData") || localStorage.getItem("userData") || "null");
		this.formulaId = this.$route.params.id;

		var self = this;
		Promise.all([this.fetchFeeds(), this.fetchRawMaterials()]).finally(function () {
			if (self.formulaId) self.fetchFormula(self.formulaId);
		});
	},
	methods: {
		goBack() {
			this.$router.go(-1);
		},

		reduceId(o) {
			return Number(o && o.id);
		},

		normalizeStr(s) {
			return (s == null ? "" : String(s)).trim().toLowerCase();
		},

		// ----- Lookups -----
		fetchFeeds() {
			var self = this;
			return axiosIns
				.get("web/feeds")
				.then(function (res) {
					var feeds =
						res && res.data && res.data.feed_list && Array.isArray(res.data.feed_list.data)
							? res.data.feed_list.data
							: [];
					self.feedOptions = feeds.map(function (f) {
						return {
							id: Number(f && f.id),
							name: f && f.name ? f.name : "Feed #" + (f && f.id != null ? f.id : ""),
						};
					});
				})
				.catch(function (err) {
					// eslint-disable-next-line no-console
					console.error(err && err.response ? err.response.data : err);
				});
		},

		fetchRawMaterials() {
			var companyId = this.userData && this.userData.company ? this.userData.company.id : null;
			var self = this;

			return axiosIns
				.get("web/company/" + companyId + "/raw-materials")
				.then(function (res) {
					var list =
						res && res.data && res.data.raw_materials && Array.isArray(res.data.raw_materials.data)
							? res.data.raw_materials.data
							: [];

					self.rawMaterialOptions = list.map(function (rm) {
						var displayName = (rm && rm.name) || (rm && rm.raw_material_name) || (rm && rm.material_name) || "";
						return {
							id: Number(rm && rm.id),
							name: displayName,
							unit: rm && rm.unit ? { id: Number(rm.unit.id), name: rm.unit.name } : null,
						};
					});
				})
				.catch(function (err) {
					// eslint-disable-next-line no-console
					console.error(err && err.response ? err.response.data : err);
				});
		},

		// add missing options from formula response so vue-select can always render labels
		ensureRawMaterialOptionsForFormulaItems(items) {
			var self = this;
			var existing = new Set(
				(self.rawMaterialOptions || []).map(function (o) {
					return Number(o && o.id);
				})
			);

			(items || []).forEach(function (it) {
				var id = it && it.raw_material_id != null ? Number(it.raw_material_id) : null;
				if (!id || existing.has(id)) return;

				var nm = (it && it.raw_material_name) || (it && it.raw_material && it.raw_material.name) || "RM #" + id;

				self.rawMaterialOptions.push({
					id: id,
					name: nm,
					unit: it && it.unit_name ? { id: it.unit_id != null ? Number(it.unit_id) : null, name: it.unit_name } : null,
				});

				existing.add(id);
			});
		},

		// ----- Load existing formula -----
		fetchFormula(id) {
			var companyId = this.userData && this.userData.company ? this.userData.company.id : null;
			var self = this;

			axiosIns
				.get("web/company/" + companyId + "/feed-formulas/" + id)
				.then(function (res) {
					var f = res && res.data && (res.data.data || res.data.formula);
					if (!f) return;

					self.form.feed_id =
						f && f.feed_id != null ? Number(f.feed_id) : f && f.feed && f.feed.id != null ? Number(f.feed.id) : null;
					self.form.version = String(f && f.version != null ? f.version : "1");
					self.form.is_active = !!(f && f.is_active);

					var items = Array.isArray(f && f.items) ? f.items : [];

					// make sure dropdown options contain selected ids
					self.ensureRawMaterialOptionsForFormulaItems(items);

					var opts = self.rawMaterialOptions || [];

					self.form.items =
						items.length > 0
							? items.map(function (it) {
									var desiredId =
										it && it.raw_material_id != null
											? Number(it.raw_material_id)
											: it && it.raw_material && it.raw_material.id != null
											? Number(it.raw_material.id)
											: null;

									var match = null;

									// id match first
									if (desiredId != null) {
										match = opts.find(function (o) {
											return Number(o && o.id) === desiredId;
										});
									}

									// fallback name match
									if (!match) {
										var nm = self.normalizeStr(
											(it && it.raw_material_name) || (it && it.raw_material && it.raw_material.name)
										);
										if (nm) {
											match = opts.find(function (o) {
												return self.normalizeStr(o && o.name) === nm;
											});
										}
									}

									return {
										uid: Date.now() + "-" + Math.random(),
										item_id: it && it.id != null ? it.id : null,
										raw_material: match || null,
										percentage: it && it.percentage != null ? Number(it.percentage).toFixed(2) : "",
									};
							  })
							: [{ uid: Date.now(), raw_material: null, percentage: "" }];
				})
				.catch(function (err) {
					// eslint-disable-next-line no-console
					console.error(err && err.response ? err.response.data : err);
				});
		},

		// ----- Helpers -----
		filteredRawMaterialOptions(idx) {
			var currentId =
				this.form.items[idx] && this.form.items[idx].raw_material && this.form.items[idx].raw_material.id != null
					? Number(this.form.items[idx].raw_material.id)
					: null;

			var selectedSet = new Set(
				this.selectedRawMaterialIds.filter(function (id) {
					return id !== currentId;
				})
			);

			return this.rawMaterialOptions.filter(function (opt) {
				return !selectedSet.has(Number(opt && opt.id));
			});
		},

		addItem() {
			this.form.items.push({
				uid: Date.now() + Math.random(),
				raw_material: null,
				percentage: "",
			});
		},

		removeItem(idx) {
			if (this.form.items.length > 1) this.form.items.splice(idx, 1);
		},

		normalizeRowPercentage(row) {
			var val = parseFloat((row.percentage || "").toString());
			if (isNaN(val)) {
				row.percentage = "";
				return;
			}
			var clamped = Math.max(0, Math.min(100, val));
			row.percentage = clamped.toFixed(2);
		},

		// ----- Save -----
		updateFormula(e) {
			e.preventDefault();
			var self = this;

			this.$refs.observer.validate().then(function (ok) {
				if (!ok) return;

				if (self.hasDuplicateSelection) {
					self.$bvToast.toast("Each raw material must be unique.", {
						title: "Validation",
						variant: "warning",
						solid: true,
					});
					return;
				}

				if (!self.isTotalValid) {
					self.$bvToast.toast("Percentages must sum to 100%.", {
						title: "Validation",
						variant: "warning",
						solid: true,
					});
					return;
				}

				self.submitting = true;

				var companyId = self.userData && self.userData.company ? self.userData.company.id : null;
				var url = "web/company/" + companyId + "/feed-formulas/" + self.formulaId;

				var payload = {
					feed_id: Number(self.form.feed_id),
					is_active: !!self.form.is_active,
					version: Number(self.form.version),
					items: self.form.items.map(function (r) {
						return {
							raw_material_id: r && r.raw_material && r.raw_material.id != null ? Number(r.raw_material.id) : null,
							percentage: Number(r.percentage),
						};
					}),
				};

				axiosIns
					.put(url, payload)
					.then(function () {
						self.$toast({
							component: ToastificationContent,
							position: "top-right",
							props: { icon: "CoffeeIcon", variant: "success", text: "Formula updated successfully" },
						});
						self.goBack();
					})
					.catch(function (error) {
						var errs =
							error && error.response && error.response?.data && error.response?.data?.errors
								? error.response?.data?.errors
								: null;
						var msg = errs
							? Object.values(errs)
									.reduce(function (acc, arr) {
										return acc.concat(arr);
									}, [])
									.join(", ")
							: "Failed to update formula";

						self.$toast({
							component: ToastificationContent,
							position: "top-right",
							props: { icon: "CoffeeIcon", variant: "danger", text: msg },
						});
					})
					.finally(function () {
						self.submitting = false;
					});
			});
		},
	},
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

.vs__dropdown-menu {
	max-height: 280px;
	overflow-y: auto;
	z-index: 2050;
}
</style>

<style>
.overflow-visible {
	overflow: visible !important;
}
</style>